/**
 * /wireline-slickline/reports — the tenant's end-of-job service reports
 * (GET /wireline-slickline/reports, fieldiq-api#1339, WL-3).
 *
 * Named "Service Reports" in the nav: the generic Reports screen is a
 * different thing. Full-width register; a row opens the report on its own
 * page. The status chips filter on the API (`status=`), so a chip's list is
 * the API's answer. A draft has no WLR- number until it is issued.
 *
 * The band counts are a tally of the rows this list returned (no endpoint
 * returns one, and the API caps the list at 500); a failed read shows `—`.
 *
 * A report is made from its job's page (Report panel), not here: there is no
 * report without a job, so this register offers no "New report".
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, FileText, PenLine } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWlMeta } from '../../hooks/useWirelineSlickline';
import { useWlReports } from '../../hooks/useWlReports';
import { formatInstantDay } from '../../lib/calendar-date';
import { queryLoadFailure } from '../../lib/load-failure';
import { wlReportNumber, wlReportStatusSkin } from '../../lib/wireline-slickline-reports';
import type { WlReport } from '../../types/wireline-slickline-reports';
import { useOpenRecord } from '../downhole/recordPage';
import { WL_EYEBROW, WlBadge, WlChip, WlChipRow, WlEmpty, WlPanel, WlRegister, type WlColumn } from './WlBits';

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'void', label: 'Void' },
];

export default function WlReportsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [status, setStatus] = useState('');
  const query = useWlReports({ status: status || undefined });
  const reports = query.data?.data ?? [];
  // GET /reports carries no `meta`; the tenant's zone comes from /meta.
  const tz = useWlMeta().data?.meta?.timeZone ?? null;
  const loading = query.isLoading;
  const known = loading || queryLoadFailure(query) ? undefined : reports;
  const issuedDay = (r: WlReport) => formatInstantDay(r.issuedAt, tz) ?? '—';

  const columns: WlColumn<WlReport>[] = [
    {
      key: 'number',
      header: 'Report',
      render: (r) => (
        <>
          <p className="font-mono font-extrabold text-white">{wlReportNumber(r)}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{r.title || 'End-of-job service report'}</p>
        </>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <WlBadge skin={wlReportStatusSkin(r.status)} value={r.status} /> },
    { key: 'job', header: 'Job', render: (r) => <span className="font-mono text-sm">{r.jobNumber || '—'}</span> },
    {
      key: 'customer',
      header: 'Customer / well',
      render: (r) => (
        <>
          <p className="text-sm text-white">{r.customerName || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">{r.wellName || '—'}</p>
        </>
      ),
    },
    { key: 'issued', header: 'Issued', render: (r) => <span className="text-sm">{issuedDay(r)}</span> },
  ];

  return (
    <div data-testid="wl-reports-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title="Service Reports"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Reports', sub: status ? 'This filter' : 'All', tone: 'neutral', loading, value: displayValue(known?.length) },
          {
            icon: <PenLine className="size-4" aria-hidden="true" />,
            label: 'Drafts',
            tone: 'accent',
            loading,
            // Counted from the loaded rows, so only meaningful when no status filter narrows them.
            value: displayValue(known && !status ? known.filter((r) => r.status === 'draft').length : undefined),
          },
          {
            icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
            label: 'Issued',
            tone: 'neutral',
            loading,
            value: displayValue(known && !status ? known.filter((r) => r.status === 'issued').length : undefined),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlChipRow label="Report status">
            <WlChip label="All" active={status === ''} onClick={() => setStatus('')} />
            {STATUSES.map((s) => (
              <WlChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} />
            ))}
          </WlChipRow>
          <WlPanel
            eyebrow="Report register"
            title="Service reports"
            testId="wl-report-register"
            footer={!loading && known && reports.length > 0 ? `${reports.length} report${reports.length === 1 ? '' : 's'}` : undefined}
          >
            <WlRegister
              query={query}
              rows={reports}
              columns={columns}
              rail={(r) => wlReportStatusSkin(r.status).rail}
              onOpen={(r) => openRecord(`/wireline-slickline/reports/${r.id}`)}
              subject="service reports"
              testId="wl-report"
              minWidth="min-w-[820px]"
              empty={
                <WlEmpty>
                  {status
                    ? 'No report matches this filter.'
                    : 'There are no service reports yet. A report is made from its job’s page once the job is under way.'}
                </WlEmpty>
              }
              card={(r) => (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-mono text-sm font-extrabold text-white">{wlReportNumber(r)}</p>
                    <WlBadge skin={wlReportStatusSkin(r.status)} value={r.status} />
                  </div>
                  <p className="mt-1 text-sm text-white">
                    {r.jobNumber || '—'} · {r.customerName || '—'}
                  </p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">{r.wellName || '—'}</p>
                  <p className="mt-2 text-xs text-fieldiq-text">Issued {issuedDay(r)}</p>
                </>
              )}
            />
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
